import React, { useState } from 'react';
import { leadGenerationAPI } from '../../services/api';

function LeadGeneration() {
  const [city, setCity] = useState('');
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedSchools, setSelectedSchools] = useState([]);
  const [editingSchool, setEditingSchool] = useState(null);
  const [editData, setEditData] = useState({});

  const scrapeSchools = async () => {
    if (!city.trim()) {
      setError('Please enter a city name');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const response = await leadGenerationAPI.scrapeSchools(city);
      setSchools(response.data.schools);
      setSelectedSchools(response.data.schools.map((_, index) => index)); // Select all by default
      setSuccess(`Found ${response.data.count} schools in ${city}`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to scrape schools');
    } finally {
      setLoading(false);
    }
  };

  const handleSchoolSelection = (index) => {
    setSelectedSchools(prev => 
      prev.includes(index) 
        ? prev.filter(i => i !== index)
        : [...prev, index]
    );
  };

  const selectAll = () => {
    setSelectedSchools(schools.map((_, index) => index));
  };

  const deselectAll = () => {
    setSelectedSchools([]);
  };

  const deleteSchool = (index) => {
    setSchools(prev => prev.filter((_, i) => i !== index));
    setSelectedSchools(prev => prev.filter(i => i !== index).map(i => i > index ? i - 1 : i));
  };

  const editSchool = (index) => {
    setEditingSchool(index);
    setEditData({ ...schools[index] });
  };

  const saveEdit = () => {
    setSchools(prev => prev.map((school, index) => 
      index === editingSchool ? editData : school
    ));
    setEditingSchool(null);
    setEditData({});
  };

  const cancelEdit = () => {
    setEditingSchool(null);
    setEditData({});
  };

  const createBulkLeads = async () => {
    if (selectedSchools.length === 0) {
      setError('Please select at least one school');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const selectedSchoolData = selectedSchools.map(index => schools[index]);
      const response = await leadGenerationAPI.createBulkLeads(selectedSchoolData);
      
      setSuccess(`Successfully created ${response.data.created} leads! Duplicates: ${response.data.duplicates}, Errors: ${response.data.errors}`);
      
      // Clear the list after successful creation
      setSchools([]);
      setSelectedSchools([]);
      setCity('');
      
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create leads');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6">
      <div className="bg-white rounded-2xl shadow-xl border border-gray-200/50 p-6">
        <div className="flex items-center space-x-4 mb-6">
          <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-xl flex items-center justify-center shadow-lg">
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <div>
            <h2 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-blue-600 bg-clip-text text-transparent">
              Lead Generation
            </h2>
            <p className="text-gray-600 mt-1">Discover schools from Google Maps and convert them to leads</p>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl mb-6">
            {error}
          </div>
        )}

        {success && (
          <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-xl mb-6">
            {success}
          </div>
        )}

        {/* Search Section */}
        <div className="bg-gray-50 rounded-xl p-6 mb-6">
          <div className="flex gap-4 items-end">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">City Name</label>
              <input
                type="text"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                placeholder="Enter city name (e.g., Mumbai, Delhi, Bangalore)"
                className="w-full p-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                onKeyPress={(e) => e.key === 'Enter' && scrapeSchools()}
              />
            </div>
            <button
              onClick={scrapeSchools}
              disabled={loading}
              className="bg-blue-500 text-white px-6 py-3 rounded-xl hover:bg-blue-600 transition disabled:opacity-50 font-medium"
            >
              {loading ? 'Searching...' : 'Search Schools'}
            </button>
          </div>
        </div>

        {/* Results Section */}
        {schools.length > 0 && (
          <>
            {/* Action Bar */}
            <div className="flex justify-between items-center mb-6 p-4 bg-blue-50 rounded-xl">
              <div className="flex items-center space-x-4">
                <span className="text-sm font-medium text-blue-800">
                  {selectedSchools.length} of {schools.length} schools selected
                </span>
                <div className="flex space-x-2">
                  <button
                    onClick={selectAll}
                    className="text-xs bg-blue-500 text-white px-3 py-1 rounded-lg hover:bg-blue-600 transition"
                  >
                    Select All
                  </button>
                  <button
                    onClick={deselectAll}
                    className="text-xs bg-gray-500 text-white px-3 py-1 rounded-lg hover:bg-gray-600 transition"
                  >
                    Deselect All
                  </button>
                </div>
              </div>
              <button
                onClick={createBulkLeads}
                disabled={loading || selectedSchools.length === 0}
                className="bg-green-500 text-white px-6 py-2 rounded-xl hover:bg-green-600 transition disabled:opacity-50 font-medium"
              >
                {loading ? 'Creating...' : `Create ${selectedSchools.length} Leads`}
              </button>
            </div>

            {/* Schools List */}
            <div className="space-y-4">
              {schools.map((school, index) => (
                <div key={school.id} className={`border rounded-xl p-4 transition-all ${
                  selectedSchools.includes(index) ? 'border-blue-300 bg-blue-50' : 'border-gray-200 bg-white'
                }`}>
                  <div className="flex items-start space-x-4">
                    <input
                      type="checkbox"
                      checked={selectedSchools.includes(index)}
                      onChange={() => handleSchoolSelection(index)}
                      className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    
                    <div className="flex-1">
                      {editingSchool === index ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">School Name</label>
                            <input
                              type="text"
                              value={editData.schoolName}
                              onChange={(e) => setEditData({...editData, schoolName: e.target.value})}
                              className="w-full p-2 border border-gray-300 rounded-lg text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Phone Number</label>
                            <input
                              type="text"
                              value={editData.phoneNumber}
                              onChange={(e) => setEditData({...editData, phoneNumber: e.target.value})}
                              className="w-full p-2 border border-gray-300 rounded-lg text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Email</label>
                            <input
                              type="email"
                              value={editData.email}
                              onChange={(e) => setEditData({...editData, email: e.target.value})}
                              className="w-full p-2 border border-gray-300 rounded-lg text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Website</label>
                            <input
                              type="text"
                              value={editData.website}
                              onChange={(e) => setEditData({...editData, website: e.target.value})}
                              className="w-full p-2 border border-gray-300 rounded-lg text-sm"
                            />
                          </div>
                          <div className="md:col-span-2">
                            <label className="block text-xs font-medium text-gray-700 mb-1">Address</label>
                            <input
                              type="text"
                              value={editData.address}
                              onChange={(e) => setEditData({...editData, address: e.target.value})}
                              className="w-full p-2 border border-gray-300 rounded-lg text-sm"
                            />
                          </div>
                          <div className="md:col-span-2 flex space-x-2">
                            <button
                              onClick={saveEdit}
                              className="bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 transition text-sm"
                            >
                              Save
                            </button>
                            <button
                              onClick={cancelEdit}
                              className="bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition text-sm"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div>
                          <div className="flex justify-between items-start mb-2">
                            <h3 className="text-lg font-bold text-gray-900">{school.schoolName}</h3>
                            <div className="flex space-x-2">
                              <button
                                onClick={() => editSchool(index)}
                                className="text-blue-500 hover:text-blue-700 text-sm"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => deleteSchool(index)}
                                className="text-red-500 hover:text-red-700 text-sm"
                              >
                                Delete
                              </button>
                            </div>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600">
                            <div>
                              <span className="font-medium">Address:</span> {school.address}
                            </div>
                            <div>
                              <span className="font-medium">Phone:</span> {school.phoneNumber}
                            </div>
                            <div>
                              <span className="font-medium">Email:</span> {school.email}
                            </div>
                            <div>
                              <span className="font-medium">Website:</span> 
                              {school.website !== 'Not available' ? (
                                <a href={school.website} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline ml-1">
                                  {school.website}
                                </a>
                              ) : (
                                <span className="ml-1">{school.website}</span>
                              )}
                            </div>
                            {school.rating > 0 && (
                              <div>
                                <span className="font-medium">Rating:</span> {school.rating} ⭐ ({school.userRatingsTotal} reviews)
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {!loading && schools.length === 0 && city && (
          <div className="text-center py-12">
            <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
              <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Schools Found</h3>
            <p className="text-gray-500">Try searching for a different city or check your spelling.</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default LeadGeneration;